import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Info, Send, UserCheck } from 'lucide-react';
import { fieldsForPhase } from '@esa/shared';
import { hasRole } from '../lib/access.js';
import { ApiError, api, type FieldDef, type Me, type TenantConfig } from '../lib/api.js';
import {
  Button,
  Callout,
  Checkbox,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Panel,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '../ui/index.js';

interface Ctx {
  tenant: string;
  me: Me | undefined;
}

/**
 * The requester form.
 *
 * The field set is rendered from the tenant's CONFIGURATION, not from code —
 * which is the whole point of the framework. Change a field in the admin UI
 * and this form changes with no deployment.
 */
export function NewRequest() {
  const { tenant, me } = useOutletContext<Ctx>();
  const nav = useNavigate();
  const toast = useToast();

  /**
   * The group this request is for.
   *
   * Seeded from the group selected in the header, and kept in step with it:
   * `useState(tenant)` reads that prop once, at mount, so switching group in
   * the header while this page is open left the form pointed at the previous
   * one — still offering its request types and, on submit, filing the ticket
   * into it. The page said Human Resources at the top and ESAtech in the
   * form, which is the shape of the bug that was reported.
   *
   * A deliberate choice in the form's own selector is not disturbed: the
   * header tenant has not changed, so this does not fire.
   */
  const [tenantId, setTenantId] = useState(tenant);
  useEffect(() => {
    setTenantId((current) => {
      if (current === tenant) return current;
      // Request type and custom fields are defined per group, so a value
      // chosen under the old one cannot carry over. Nor can the on-behalf-of
      // address: whether it may be sent at all is this group's setting and
      // this group's role, and a value left behind would be filed under the
      // wrong person the moment the next group happens to allow it.
      setRequestType('');
      setCustom({});
      setRequesterEmail('');
      return tenant;
    });
  }, [tenant]);
  const [requestType, setRequestType] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjectEmail, setSubjectEmail] = useState('');
  /** On-behalf-of: who is ASKING, when that is not the person at the keyboard. */
  const [requesterEmail, setRequesterEmail] = useState('');
  const [custom, setCustom] = useState<Record<string, unknown>>({});

  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: { id: string; name: string }[] }>('/api/v1/tenants'),
  });

  const cfg = useQuery({
    queryKey: ['config', tenantId, requestType],
    queryFn: () =>
      api.get<TenantConfig>(
        `/api/v1/tenants/${tenantId}/config?requestType=${encodeURIComponent(requestType)}`,
      ),
    enabled: Boolean(tenantId),
  });

  /**
   * May this person file FOR someone else, in the group the form is pointed
   * at?
   *
   * Two conditions, both the server's: staff only (a requester filing as a
   * colleague is impersonation), and only where the group has turned it on.
   * The role is read per GROUP rather than from the header's tenant, because
   * this form can be pointed at a group the person is only a requester in —
   * offering the field there would produce a 403 on submit.
   *
   * This decides what to OFFER. The server re-checks both.
   */
  const roleHere = me?.tenants.find((t) => t.id === tenantId)?.role ?? null;
  const canFileForOthers =
    hasRole(roleHere, 'agent') && cfg.data?.settings.allowOnBehalfOf === true;
  const onBehalfOf = canFileForOthers ? requesterEmail.trim() : '';

  const create = useMutation({
    mutationFn: () =>
      api.post<{ number: number }>(`/api/v1/tenants/${tenantId}/tickets`, {
        subject,
        body,
        requestType: requestType || undefined,
        subjectEmail: subjectEmail || undefined,
        // Omitted entirely when empty: '' is a value, and the server would
        // read it as an on-behalf-of submission for nobody.
        requesterEmail: onBehalfOf || undefined,
        custom,
        source: 'portal',
      }),
    onSuccess: (r) => {
      toast.success(
        `Request #${r.number} submitted`,
        onBehalfOf ? `Filed on behalf of ${onBehalfOf}` : undefined,
      );
      nav(`/t/${tenantId}/${r.number}`);
    },
  });

  /**
   * What belongs on a form for RAISING a request.
   *
   * `/config` returns every field, deliberately — the resolve dialog needs
   * the close ones. This form does not: a resolution code on a new request
   * asks how something was fixed before it has been reported.
   */
  const fields = fieldsForPhase(cfg.data?.fields ?? [], 'create');
  const visible = fields.filter(
    (f) => !f.showWhen || custom[f.showWhen.field] === f.showWhen.equals,
  );

  /**
   * The server's own words about the address, shown against the field.
   *
   * Not rephrased: "no such ESA identity" is the actual reason and the reader
   * can act on it, where a generic "invalid email" would send them off
   * checking the spelling of an address that is spelled correctly and simply
   * belongs to nobody.
   */
  const requesterIssue =
    create.error instanceof ApiError
      ? create.error.fields?.find((f) => f.key === 'requesterEmail')?.message
      : undefined;

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <PageHeader
        title="New request"
        description="Pick the group and what you need; the form adapts to that group's configuration."
      />

      <Panel>
        <div className="space-y-4">
          <Field label="Group" hint="You do not need to know which team ends up doing the work.">
            <Select
              value={tenantId}
              onValueChange={(v) => {
                setTenantId(v);
                setRequestType('');
                setCustom({});
                setRequesterEmail('');
              }}
              options={(tenants.data?.tenants ?? []).map((t) => ({
                value: t.id,
                label: t.name,
              }))}
              placeholder="Choose a group"
            />
          </Field>

          <Field label="What do you need?">
            <Select
              value={requestType}
              onValueChange={(v) => {
                setRequestType(v);
                setCustom({});
              }}
              placeholder="Not sure — let the desk decide"
              options={[
                { value: '', label: 'Not sure — let the desk decide' },
                ...(cfg.data?.requestTypes.map((rt) => ({ value: rt.label, label: rt.label })) ??
                  []),
              ]}
            />
          </Field>

          <Field label="Summary" required>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="One line — what do you need?"
            />
          </Field>

          <Field
            label="Detail"
            hint="Anything that saves us coming back to you: project number, file path, deadline."
          >
            <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>

          <Field
            label="Is this about someone else?"
            hint="Onboarding and access requests are usually about a colleague. Saying so stops us provisioning the wrong person."
          >
            <Input
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              placeholder="their.email@esassoc.com"
            />
          </Field>

          {/*
            Staff only, and only where the group allows it. A requester must
            never see this: to them the question "who is this from?" has one
            answer and offering a box invites them to put someone else in it.
          */}
          {canFileForOthers && (
            <Field
              label="Filing this for someone else?"
              hint="Leave empty unless you are taking this down for a colleague — over the phone, at a desk, in a corridor."
              error={requesterIssue}
            >
              <Input
                type="email"
                value={requesterEmail}
                onChange={(e) => setRequesterEmail(e.target.value)}
                placeholder="the.person.asking@esassoc.com"
              />
            </Field>
          )}
        </div>

        {/*
          Who the ticket ends up belonging to, said out loud.

          Filing under the wrong person is the failure mode here and it is
          otherwise silent: the ticket is created, the confirmation goes to
          somebody who did not ask for it, and the person who did ask cannot
          see their own request. Only staff can reach that state, so only
          staff are told about it.
        */}
        {canFileForOthers && (
          <Callout
            tone={onBehalfOf ? 'warn' : 'neutral'}
            icon={<UserCheck />}
            className="mt-4"
            title={onBehalfOf ? `This request will belong to ${onBehalfOf}` : 'This request will be yours'}
          >
            {onBehalfOf
              ? 'They become the requester: the confirmation, the updates and the ability to reply are theirs, not yours.'
              : `Filed as ${me?.name ?? me?.email ?? 'you'}. Put a colleague's address above to file it as theirs instead.`}
          </Callout>
        )}

        {cfg.isLoading && (
          <div className="mt-4 space-y-3 border-t border-line-subtle pt-4">
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-8 w-full" />
          </div>
        )}

        {visible.length > 0 && (
          <div className="mt-5 space-y-4 border-t border-line-subtle pt-5">
            <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
              <Info aria-hidden className="h-3 w-3" />
              {cfg.data?.name} needs
            </p>
            {visible.map((f) => (
              <CustomField
                key={f.key}
                def={f}
                value={custom[f.key]}
                onChange={(v) => setCustom({ ...custom, [f.key]: v })}
              />
            ))}
          </div>
        )}

        {create.error && (
          <div className="mt-4">
            <ErrorState error={create.error} />
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-line-subtle pt-4">
          <p className="text-2xs text-ink-tertiary">
            {onBehalfOf
              ? `${onBehalfOf} will get the email confirmation with the ticket reference.`
              : 'You will get an email confirmation with the ticket reference.'}
          </p>
          <Button
            variant="primary"
            size="lg"
            leading={<Send className="h-3.5 w-3.5" />}
            loading={create.isPending}
            disabled={subject.trim().length === 0}
            onClick={() => create.mutate()}
          >
            Submit request
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function CustomField({
  def,
  value,
  onChange,
}: {
  def: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const required = def.requiredAt === 'create';

  if (def.fieldType === 'checkbox') {
    return (
      <Checkbox
        checked={value === true}
        onCheckedChange={onChange}
        label={def.label}
      />
    );
  }

  if (def.fieldType === 'select') {
    return (
      <Field label={def.label} required={required}>
        <Select
          value={(value as string) ?? ''}
          onValueChange={onChange}
          placeholder="Choose…"
          options={(def.validation.options ?? []).map((o) => ({ value: o, label: o }))}
        />
      </Field>
    );
  }

  const type =
    def.fieldType === 'number'
      ? 'number'
      : def.fieldType === 'date'
        ? 'date'
        : def.fieldType === 'email'
          ? 'email'
          : def.fieldType === 'url'
            ? 'url'
            : 'text';

  return (
    <Field label={def.label} required={required}>
      <Input
        type={type}
        value={(value as string) ?? ''}
        onChange={(e) =>
          onChange(def.fieldType === 'number' ? Number(e.target.value) : e.target.value)
        }
        placeholder={def.fieldType === 'project_number' ? 'D202401044.00' : undefined}
      />
    </Field>
  );
}
